import { LoadingCard } from '@/components/Base/LoadingCard/LoadingCard'
import { NoDataToDisplayCard } from '@/components/Base/NoDataToDisplayCard/NoDataToDisplayCard'
import ReportSchedulesTopNav from '@/components/ReportDashboard/ReportSchedules/ReportSchedulesTopNav'
import { RunScenarioModal } from '@/components/RunScenarioModal/RunScenarioModal'
import ScheduledReportsList from '@/components/ScheduledReports/ScheduledReportsList'
import { Toaster } from '@/components/Shared/Toast'
import { createTheme } from '@/constants/risk-dashboard-colors'
import RADashboardThemeContextProvider from '@/contexts/RADashboardThemeContext'
import { UserPermission } from '@/enums/scenario-builder.enum'
import { useAppDispatch } from '@/hooks/use-app-dispatch'
import { useAppSelector } from '@/hooks/use-app-selector'
import { usePortfolioAndBenchmarkLists } from '@/hooks/usePortfolioAndBenchmarkLists'
import { useSchemaVersionGate } from '@/hooks/useSchemaVersionGate'
import { IScheduledReportsDTO2 } from '@/interfaces/scenarioAnalysisReportComponents.interface'
import {
  deleteScheduledReport2,
  getScheduledReports2,
} from '@/services/axiomReport.service'
import {
  getLoggedInUserInfo,
  userHasPermission,
} from '@/services/scenarioBuilder.service'
import { getUserPreference, putUserPreference } from '@/services/userPreference.service'
import {
  clearState,
  setUserInfo,
  toggleRunReportTemplateModalOpened,
} from '@/states/scenarioBuilder.slice'
import { ThemeProvider, createTheme as createMuiTheme } from '@mui/material'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'

const DL_MODE_CONTEXT = 'reportDashboard.DLMode'

interface DLModePreference {
  isDarkMode: boolean
}

const ReportSchedulesPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const userInfo = useAppSelector((s) => s.scenarioBuilder.userInfo)
  const isRunReportTemplateModalOpened = useAppSelector(
    (s) => s.scenarioBuilder.isRunReportTemplateModalOpened,
  )

  const [isDarkMode, setIsDarkMode] = useState<boolean>(false)
  const [prefLoaded, setPrefLoaded] = useState<boolean>(false)
  const [userInfoLoaded, setUserInfoLoaded] = useState<boolean>(false)
  const [scheduledReports, setScheduledReports] = useState<
    IScheduledReportsDTO2[]
  >([])
  const [scheduledReportsLoading, setScheduledReportsLoading] =
    useState<boolean>(true)
  const hasCheckedUser = useRef(false)
  const wasRunReportTemplateModalOpened = useRef(false)

  const theme = createTheme(isDarkMode)
  const muiTheme = useMemo(
    () =>
      createMuiTheme({
        typography: { fontFamily: theme.fontFamily },
        palette: {
          mode: isDarkMode ? 'dark' : 'light',
          primary: {
            main: '#0a5eb2', //--cwan-palette-primary-main
            dark: '#0a5eb2', //--cwan-palette-primary-main
          },
          action: {
            hover: theme.buttonBackground,
          },
          text: {
            primary: theme.textPrimary,
          },
        },
      }),
    [isDarkMode],
  )

  // Load dark-mode preference on mount. Shares the same preference key as the
  // other Factor Analytics pages so the toggle stays in sync between them.
  useEffect(() => {
    getUserPreference<DLModePreference>(DL_MODE_CONTEXT)
      .then((pref) => {
        if (pref?.isDarkMode !== undefined) {
          setIsDarkMode(pref.isDarkMode)
        }
      })
      .finally(() => setPrefLoaded(true))
  }, [])

  // Same bootstrapping as ScenarioBuilderPage: reset the shared scenarioBuilder
  // slice and load the current user so permissions can be checked.
  useEffect(() => {
    if (hasCheckedUser.current) return
    hasCheckedUser.current = true
    dispatch(clearState())
    getLoggedInUserInfo()
      .then((info) => dispatch(setUserInfo(info)))
      .catch((error) =>
        console.error('Failed to fetch user information:', error),
      )
      .finally(() => setUserInfoLoaded(true))
  }, [dispatch])

  const schemaVersionGate = useSchemaVersionGate()

  const canView = userHasPermission(userInfo, UserPermission.VIEW_REPORT_TEMPLATE)
  const canCreateSchedule =
    userHasPermission(userInfo, UserPermission.RUN_SCENARIO) ||
    userHasPermission(userInfo, UserPermission.SCHEDULE_SCENARIO)

  // Once permitted, load (or refresh, if stale) the portfolio/benchmark lists
  // the Create Report modal needs (it reads them straight from the
  // scenarioBuilder Redux slice).
  usePortfolioAndBenchmarkLists(canView)

  const fetchScheduledReports = async () => {
    try {
      setScheduledReportsLoading(true)
      const data = await getScheduledReports2()
      setScheduledReports(data ?? [])
    } catch (error) {
      console.warn(error)
    } finally {
      setScheduledReportsLoading(false)
    }
  }

  useEffect(() => {
    if (!canView) return
    fetchScheduledReports()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView])

  // The Create Report modal closes on both successful submit and cancel; either
  // way, refresh the table so a newly scheduled report shows up right away.
  useEffect(() => {
    if (wasRunReportTemplateModalOpened.current && !isRunReportTemplateModalOpened) {
      fetchScheduledReports()
    }
    wasRunReportTemplateModalOpened.current = isRunReportTemplateModalOpened
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunReportTemplateModalOpened])

  const handleDeleteScheduledReport = async (scheduleId: number) => {
    try {
      await deleteScheduledReport2(scheduleId)
      await fetchScheduledReports()
    } catch (error) {
      console.error('Failed to delete scheduled report:', error)
      toast.error('Failed to delete scheduled report. Please try again later.')
    }
  }

  const handleDarkModeToggle = (isDark: boolean) => {
    setIsDarkMode(isDark)
    putUserPreference<DLModePreference>(DL_MODE_CONTEXT, { isDarkMode: isDark })
  }

  if (!prefLoaded || !userInfoLoaded || !schemaVersionGate.loaded) {
    return (
      <RADashboardThemeContextProvider isDarkMode={isDarkMode} theme={theme}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            backgroundColor: theme.background,
            color: theme.textPrimary,
          }}
        />
        <Toaster />
      </RADashboardThemeContextProvider>
    )
  }

  if (!canView || !schemaVersionGate.isFactorAnalyticsEnabled) {
    return (
      <RADashboardThemeContextProvider isDarkMode={isDarkMode} theme={theme}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            backgroundColor: theme.background,
            color: theme.textPrimary,
            padding: '24px',
            textAlign: 'center',
          }}
        >
          There is a policy or version mismatch, this page is not viewable.
          Please contact support.
        </div>
        <Toaster />
      </RADashboardThemeContextProvider>
    )
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <RADashboardThemeContextProvider isDarkMode={isDarkMode} theme={theme}>
        <div
          style={{
            background: theme.background,
            color: theme.textPrimary,
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <ReportSchedulesTopNav
            isDarkMode={isDarkMode}
            onDarkModeToggle={handleDarkModeToggle}
          />

          <div
            style={{
              padding: '0px 24px 24px',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginBottom: '24px',
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  color: theme.textPrimary,
                }}
              >
                Scheduled Reports
              </div>

              {canCreateSchedule && (
                <button
                  onClick={() =>
                    dispatch(toggleRunReportTemplateModalOpened(true))
                  }
                  style={{
                    marginLeft: 'auto',
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    backgroundColor: theme.runButtonBackground,
                    color: theme.runButtonColor,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Create Schedule
                </button>
              )}
            </div>

            <div
              style={{
                backgroundColor: theme.cardBackground,
                border: `1px solid ${theme.border}`,
                borderRadius: '8px',
                overflow: 'hidden',
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                boxShadow: isDarkMode
                  ? '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)'
                  : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              }}
            >
              {scheduledReportsLoading && <LoadingCard />}
              {!scheduledReportsLoading && !scheduledReports.length && (
                <NoDataToDisplayCard />
              )}
              {!scheduledReportsLoading && scheduledReports.length > 0 && (
                <ScheduledReportsList
                  rowData={scheduledReports}
                  onDelete={handleDeleteScheduledReport}
                  isDarkMode={isDarkMode}
                />
              )}
            </div>
          </div>

          <RunScenarioModal />
          <Toaster />
        </div>
      </RADashboardThemeContextProvider>
    </ThemeProvider>
  )
}

export default ReportSchedulesPage
